import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { membershipService } from 'services/api';
import MembershipEditModal from './MembershipEditModal';
import MembershipRenewModal from './MembershipRenewModal';
import MembershipPlanModal from './MembershipPlanModal';
import ConfirmActionModal from 'components/ConfirmActionModal';
import Paginacion from 'components/Paginacion';
import styles from './Memberships.module.css';

const STATUS_COLOR = { active: '#12b76a', trial: '#f79009', inactive: '#f04438' };
const STATUS_BG    = { active: '#12b76a18', trial: '#f7900918', inactive: '#f0443818' };
const STATUS_LABEL = { active: 'Activa', trial: 'Trial', inactive: 'Inactiva' };
const STATUS_ICON  = { active: 'fa-circle-check', trial: 'fa-clock', inactive: 'fa-circle-xmark' };

// Exportados porque MembershipRenewModal/MembershipPlanModal también los usan.
export const RENEW_OPTIONS = [
  { label: '1 mes',   days: 30 },
  { label: '3 meses', days: 90 },
  { label: '6 meses', days: 180 },
  { label: '1 año',   days: 365 },
  { label: '2 años',  days: 730 },
];

// F-05: asambleas que incluye un período de Estándar/Premium. MISMA fórmula
// que computeAssemblyAllotment (backend/src/utils/subscriptionStatus.js):
// floor(días / 365 × tasa anual), standard 1/año, premium 3/año — 1 y 3 meses
// dan 0 (regla confirmada por Marlon). Si cambia allá, cambia aquí.
const TASA_ANUAL_ASAMBLEAS = { standard: 1, premium: 3 };
export function asambleasIncluidas(tipo, dias) {
  const tasa = TASA_ANUAL_ASAMBLEAS[tipo];
  const d = parseInt(dias);
  if (tasa === undefined || !Number.isInteger(d) || d < 1) return null;
  return Math.floor((d / 365) * tasa);
}
export const txtAsambleas = (n) => `${n} asamblea${n === 1 ? '' : 's'}`;

// Tipos de plan — el número de asambleas por ciclo (365 días desde la
// activación) debe coincidir con PLAN_ASSEMBLY_LIMITS en
// backend/src/utils/subscriptionStatus.js.
export const PLAN_OPTIONS = [
  { key: 'standard',  label: 'Estándar',             color: 'var(--accent)', icon: 'fa-star',        desc: 'Acceso completo de administración · hasta 1 asamblea/año (según período)' },
  { key: 'premium',   label: 'Premium',              color: '#e85520',       icon: 'fa-crown',        desc: 'Acceso completo de administración · hasta 3 asambleas/año (según período)' },
  { key: 'asamblea',  label: 'Asamblea',             color: '#6c63ff',       icon: 'fa-gavel',        desc: '2 semanas · Privilegios de Admin Asamblea · 1 asamblea' },
  { key: 'logistica', label: 'Asamblea + Logística', color: '#059669',       icon: 'fa-boxes-stacked', desc: 'Gestión de propiedad sin módulo de asambleas · 1 asamblea' },
];

export function daysLeft(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
}

// ── Estado REAL considerando fecha de vencimiento ─────────────────────────────
function resolveStatus(p) {
  if (!p.subscription_status) return 'inactive';
  if (p.subscription_expires_at) {
    const expired = new Date(p.subscription_expires_at) < new Date();
    if (expired && (p.subscription_status === 'trial' || p.subscription_status === 'active')) {
      return 'inactive';
    }
  }
  return p.subscription_status;
}

export function StatusBadge({ status, expires_at }) {
  const days    = daysLeft(expires_at);
  const expired = days !== null && days < 0;
  // Si venció, siempre mostrar como inactiva independientemente del status en BD
  const realStatus = expired ? 'inactive' : status;
  const color  = STATUS_COLOR[realStatus] || '#888';
  const bg     = STATUS_BG[realStatus]    || '#eee';
  const label  = expired ? 'Vencida' : STATUS_LABEL[realStatus] || status;
  const icon   = STATUS_ICON[realStatus]  || 'fa-circle';
  return (
    <span className={styles.insigniaEstado} style={{ '--tono': color, '--fondo': bg }}>
      <i className={`fas ${icon} ${styles.insigniaIcono}`} />{label}
    </span>
  );
}

export function ExpiryCell({ expires_at }) {
  if (!expires_at) return <span className={styles.sinFecha}>Sin fecha</span>;
  const days = daysLeft(expires_at);
  const date = new Date(expires_at).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  let tag = null;
  if (days !== null && days < 0)
    tag = <span className={styles.etiquetaVencida}>Vencida</span>;
  else if (days !== null && days <= 30)
    tag = <span className={styles.etiquetaPorVencer}>({days}d)</span>;
  return <span className={styles.fecha}>{date}{tag}</span>;
}

// ── Página principal ──────────────────────────────────────────────────────────

export default function Memberships() {
  const [items,     setItems]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [alert,     setAlert]     = useState(null);
  const [search,    setSearch]    = useState('');
  // El dashboard enlaza aquí con ?filtro=por-vencer (el chip «Por vencer» ya activo).
  const [searchParams] = useSearchParams();
  const [filter,    setFilter]    = useState(searchParams.get('filtro') === 'por-vencer' ? 'expiring' : '');
  const [pagina,    setPagina]    = useState(1);
  const [editProp,  setEditProp]  = useState(null);
  const [renewProp, setRenewProp] = useState(null);
  const [planProp,  setPlanProp]  = useState(null);
  const [extraAssemblyProp, setExtraAssemblyProp] = useState(null);
  const [addingExtra,       setAddingExtra]       = useState(false);
  const [extraError,        setExtraError]        = useState('');

  const load = () => {
    setLoading(true);
    const params = {};
    if (filter === 'expiring') params.expiring_days = 30;
    else if (filter)           params.status = filter;
    if (search)                params.search = search;
    membershipService.getAll(params)
      .then(r => { setItems(r.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { setPagina(1); load(); }, [filter]); // eslint-disable-line
  useEffect(() => {
    const t = setTimeout(() => { setPagina(1); load(); }, 400);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const countUnits = (p) => p.units_count || 0;

  // La lista llega completa del backend (los contadores de arriba la cuentan entera):
  // aquí solo se corta lo que se pinta. Si tras guardar la lista se acorta, no queda una página vacía.
  const POR_PAGINA = 10;
  const paginaActual = Math.min(pagina, Math.max(1, Math.ceil(items.length / POR_PAGINA)));
  const irAPagina = (n) => { setPagina(n); window.scrollTo({ top: 0 }); };

  const onSaved = (msg) => {
    setAlert({ type: 'success', message: msg });
    setEditProp(null); setRenewProp(null); setPlanProp(null);
    load();
  };

  // MEMB-03: cuando el conjunto ya usó el tope de asambleas de su plan y
  // compra una adicional — no toca la fecha de vencimiento de la membresía.
  const confirmExtraAssembly = async () => {
    if (!extraAssemblyProp) return;
    setAddingExtra(true);
    setExtraError('');
    try {
      await membershipService.addExtraAssembly(extraAssemblyProp.id, 1);
      setAlert({ type: 'success', message: `Asamblea extra agregada a ${extraAssemblyProp.name}` });
      setExtraAssemblyProp(null);
      load();
    } catch (err) {
      setExtraError(err.response?.data?.message || 'No se pudo agregar la asamblea extra. Inténtalo de nuevo.');
    } finally {
      setAddingExtra(false);
    }
  };

  // ── Contadores usando estado REAL ──────────────────────────────────────────
  const counts = {
    all:      items.length,
    active:   items.filter(p => resolveStatus(p) === 'active').length,
    trial:    items.filter(p => resolveStatus(p) === 'trial').length,
    inactive: items.filter(p => resolveStatus(p) === 'inactive').length,
    expiring: items.filter(p => { const d = daysLeft(p.subscription_expires_at); return d !== null && d >= 0 && d <= 30; }).length,
  };

  const filterTabs = [
    { key: '',         label: 'Todas',         count: counts.all,      color: 'var(--accent)' },
    { key: 'active',   label: 'Activas',       count: counts.active,   color: '#12b76a' },
    { key: 'trial',    label: 'Trial',         count: counts.trial,    color: '#f79009' },
    { key: 'inactive', label: 'Inactivas',     count: counts.inactive, color: '#f04438' },
    { key: 'expiring', label: 'Vencen pronto', count: counts.expiring, color: '#ff6b35' },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <h1><i className={`fas fa-id-card ${styles.iconoTitulo}`} />Membresías</h1>
          <p>Gestiona el estado y vigencia de las membresías de cada propiedad</p>
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type} ${styles.alertaClicable}`} onClick={() => setAlert(null)}>
          {alert.message}
        </div>
      )}

      {/* KPI cards */}
      <div className={styles.kpis}>
        {[
          { label: 'Total',         value: counts.all,      color: '#6c63ff', icon: 'fa-building' },
          { label: 'Activas',       value: counts.active,   color: '#12b76a', icon: 'fa-circle-check' },
          { label: 'En trial',      value: counts.trial,    color: '#f79009', icon: 'fa-clock' },
          { label: 'Inactivas',     value: counts.inactive, color: '#f04438', icon: 'fa-circle-xmark' },
          { label: 'Vencen en 30d', value: counts.expiring, color: '#ff6b35', icon: 'fa-triangle-exclamation' },
        ].map(k => (
          <div key={k.label} className={styles.kpi} style={{ '--tono': k.color, '--fondo': k.color + '22' }}>
            <div className={styles.kpiCaja}>
              <i className={`fas ${k.icon} ${styles.kpiIcono}`} />
            </div>
            <div>
              <div className={styles.kpiValor}>{k.value}</div>
              <div className={styles.kpiEtiqueta}>{k.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        {/* Filtros y búsqueda */}
        <div className={styles.barraFiltros}>
          <div className={styles.chips}>
            {filterTabs.map(tab => (
              <button key={tab.key}
                className={filter === tab.key ? `btn btn-sm btn-primary ${styles.chipActivo}` : 'btn btn-sm btn-secondary'}
                onClick={() => setFilter(tab.key)}
                style={{ '--tono': tab.color }}>
                {tab.label}
                <span className={filter === tab.key ? `${styles.chipContador} ${styles.chipContadorActivo}` : styles.chipContador}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
          <div className={styles.buscadorCaja}>
            <i className={`fas fa-search ${styles.buscadorLupa}`} />
            <input type="text" className={`form-control ${styles.buscador}`} placeholder="Buscar propiedad..."
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Tabla */}
        {loading ? <div className="loading"><div className="spinner" /></div> :
          items.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-id-card" />
              <p>Sin propiedades para mostrar</p>
            </div>
          ) : (
            <>
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Propiedad</th>
                    <th>Administrador</th>
                    <th>Unidades</th>
                    <th>Estado</th>
                    <th>Vencimiento</th>
                    <th className={styles.thCentro}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA).map(p => {
                    const days    = daysLeft(p.subscription_expires_at);
                    const admin   = p.admins?.[0]?.user;
                    const units   = countUnits(p);
                    const warning = days !== null && days >= 0 && days <= 30;
                    const expired = days !== null && days < 0;
                    return (
                      <tr key={p.id} className={expired ? styles.filaVencida : warning ? styles.filaPorVencer : undefined}>
                        <td>
                          <div className={styles.nombre}>{p.name}</div>
                          <div className={styles.direccion}>{p.address}</div>
                        </td>
                        <td>
                          {admin ? (
                            <div>
                              <div className={styles.adminNombre}>{admin.name} {admin.last_name || ''}</div>
                              <div className={styles.adminCorreo}>{admin.email}</div>
                            </div>
                          ) : <span className={styles.sinAsignar}>Sin asignar</span>}
                        </td>
                        <td className={styles.celdaCentro}>
                          <span className="badge badge-secondary">{units}</span>
                        </td>
                        <td>
                          <StatusBadge status={p.subscription_status} expires_at={p.subscription_expires_at} />
                          {p.subscription_type === 'asamblea' && (
                            <div className={styles.etiquetaCaja}>
                              <span className={`${styles.etiquetaPlan} ${styles.planAsamblea}`}>
                                <i className={`fas fa-gavel ${styles.etiquetaIcono}`} />Asamblea
                              </span>
                            </div>
                          )}
                          {p.subscription_type === 'logistica' && (
                            <div className={styles.etiquetaCaja}>
                              <span className={`${styles.etiquetaPlan} ${styles.planLogistica}`}>
                                <i className={`fas fa-boxes-stacked ${styles.etiquetaIcono}`} />Asm + Logística
                              </span>
                            </div>
                          )}
                          {p.subscription_type === 'premium' && (
                            <div className={styles.etiquetaCaja}>
                              <span className={`${styles.etiquetaPlan} ${styles.planPremium}`}>
                                <i className={`fas fa-crown ${styles.etiquetaIcono}`} />Premium
                              </span>
                            </div>
                          )}
                          {p.extra_assemblies > 0 && (
                            <div className={styles.etiquetaCaja}>
                              <span className={`${styles.etiquetaPlan} ${styles.planExtra}`}>
                                <i className={`fas fa-plus ${styles.etiquetaIcono}`} />{p.extra_assemblies} asamblea(s) extra
                              </span>
                            </div>
                          )}
                        </td>
                        <td>
                          <ExpiryCell expires_at={p.subscription_expires_at} />
                        </td>
                        <td>
                          <div className={`actions ${styles.acciones}`}>
                            <button className="btn btn-sm btn-secondary btn-icon" title="Editar membresía"
                              onClick={() => setEditProp(p)}>
                              <i className="fas fa-pencil" />
                            </button>
                            <button title="Renovar membresía"
                              onClick={() => setRenewProp(p)}
                              className={`${styles.botonAccion} ${styles.botonRenovar}`}>
                              <i className="fas fa-rotate-right" /> Renovar
                            </button>
                            <button title="Cambiar plan"
                              onClick={() => setPlanProp(p)}
                              className={`${styles.botonAccion} ${styles.botonPlan}`}>
                              <i className={`fas fa-layer-group ${styles.botonIcono}`} /> Plan
                            </button>
                            <button title="Agregar asamblea extra (no modifica el vencimiento de la membresía)"
                              onClick={() => setExtraAssemblyProp(p)}
                              className={`${styles.botonAccion} ${styles.botonAsamblea}`}>
                              <i className={`fas fa-gavel ${styles.botonIcono}`} /> +Asamblea
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )
        }
      </div>
      {!loading && <Paginacion pagina={paginaActual} total={items.length} porPagina={POR_PAGINA} onCambiar={irAPagina} />}

      {editProp  && <MembershipEditModal  property={editProp}  onClose={() => setEditProp(null)}  onSaved={onSaved} />}
      {renewProp && <MembershipRenewModal property={renewProp} onClose={() => setRenewProp(null)} onSaved={onSaved} />}
      {planProp  && <MembershipPlanModal  property={planProp}  onClose={() => setPlanProp(null)}  onSaved={onSaved} />}

      {extraAssemblyProp && (
        <ConfirmActionModal title="Agregar asamblea extra" onClose={() => { setExtraAssemblyProp(null); setExtraError(''); }} onConfirm={confirmExtraAssembly}
          loading={addingExtra} error={extraError} confirmLabel="Sí, agregar" confirmIcon="fa-gavel">
          <div className={styles.preguntaAsamblea}>
            ¿Agregar 1 asamblea extra a <span className={styles.nombreAcento}>{extraAssemblyProp.name}</span>?
          </div>
        </ConfirmActionModal>
      )}
    </div>
  );
}
